import { Injectable, inject, signal } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import type { BannerType } from '@/design-system/molecules/banner/banner.component';

export interface Toast {
  id: string;
  type: BannerType;
  title: string;
  description?: string;
  durationMs: number;
  dismissing: boolean;
}

export interface ToastOptions {
  type: BannerType;
  title: string;
  titleParams?: Record<string, unknown>;
  description?: string;
  durationMs?: number;
}

const DEFAULT_DURATION_MS = 5000;
const MAX_TOASTS = 5;
const DISMISS_ANIMATION_MS = 350;

@Injectable({ providedIn: 'root' })
export class ToastStore {
  private readonly transloco = inject(TranslocoService);
  private _toasts = signal<Toast[]>([]);

  public toasts = this._toasts.asReadonly();

  show(options: ToastOptions): void {
    const toast: Toast = {
      id: crypto.randomUUID(),
      type: options.type,
      title: this.transloco.translate(options.title, options.titleParams),
      description: options.description !== undefined ? this.transloco.translate(options.description) : undefined,
      durationMs: options.durationMs ?? DEFAULT_DURATION_MS,
      dismissing: false,
    };

    this._toasts.update((current) => {
      const next = [...current, toast];
      return next.length > MAX_TOASTS ? next.slice(next.length - MAX_TOASTS) : next;
    });

    setTimeout(() => {
      this.startDismiss(toast.id);
    }, toast.durationMs);
  }

  startDismiss(id: string): void {
    this._toasts.update((current) => current.map((t) => (t.id === id ? { ...t, dismissing: true } : t)));

    setTimeout(() => {
      this.dismiss(id);
    }, DISMISS_ANIMATION_MS);
  }

  dismiss(id: string): void {
    this._toasts.update((current) => current.filter((t) => t.id !== id));
  }
}
